import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Plus,
  Trash2,
  Star,
  LogOut,
  RefreshCw,
  Eye,
  Link as LinkIcon,
  Image,
  Mail,
  KeyRound,
  Inbox,
  Phone,
  MapPin,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { applyDocLang, Brand, LoadingScreen } from "@/components/brand";
import { useSeo } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEMO_STATUSES = [
  { v: "new", label: "Yeni", cls: "chip-violet" },
  { v: "contacted", label: "Arandı", cls: "chip-pending" },
  { v: "won", label: "Kazanıldı", cls: "chip-resolved" },
  { v: "lost", label: "Kaybedildi", cls: "chip-danger" },
];

const storage = {
  get: (k) => {
    try {
      return localStorage.getItem(k) || "";
    } catch (e) {
      return "";
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch (e) {
      /* ignore */
    }
  },
  del: (k) => {
    try {
      localStorage.removeItem(k);
    } catch (e) {
      /* ignore */
    }
  },
};

// ---------- Login ----------
const MasterLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/master/login`, { password });
      if (res.data.success) {
        storage.set("masterToken", res.data.token);
        onLogin(res.data.token);
      }
    } catch (error) {
      toast.error(error.response?.status === 503 ? "Master girişi sunucuda yapılandırılmamış" : "Şifre hatalı");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="scene grid place-items-center" style={{ minHeight: "100dvh" }}>
      <div className="floor" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="wrap"
        style={{ maxWidth: 440 }}
      >
        <div className="panel panel-rim" style={{ padding: "2.4rem 2rem" }}>
          <Brand />
          <h1 className="display mt-8" style={{ fontSize: "2.3rem" }}>Yönetim konsolu</h1>
          <p className="muted mt-1 mb-8">Oteller ve demo talepleri</p>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <div>
              <label className="field-label" htmlFor="mpw">Master şifre</label>
              <input
                id="mpw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field"
                autoComplete="current-password"
                data-testid="master-password-input"
                autoFocus
              />
            </div>
            <button type="submit" disabled={isLoading || !password} className="btn btn-primary btn-block" data-testid="master-login-button">
              {isLoading ? "…" : "Giriş"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// ---------- Add hotel ----------
const AddHotelModal = ({ onClose, onAdd }) => {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    logo_url: "",
    google_maps_url: "",
    admin_password: "",
    notification_email: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const slugify = (s) =>
    s
      .toLowerCase()
      .replace(/ı/g, "i")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || form.admin_password.length < 8) {
      toast.error("Ad, kimlik ve en az 8 karakterli şifre gerekli");
      return;
    }
    setIsLoading(true);
    try {
      await onAdd(form);
      onClose();
    } catch (error) {
      /* handled by parent */
    } finally {
      setIsLoading(false);
    }
  };

  const fields = [
    { k: "logo_url", label: "Logo URL", icon: Image, type: "url", ph: "https://…/logo.png" },
    { k: "google_maps_url", label: "Google yorum bağlantısı", icon: Star, type: "url", ph: "https://g.page/r/…/review" },
    { k: "notification_email", label: "Bildirim e-postası", icon: Mail, type: "email", ph: "resepsiyon@otel.com" },
  ];

  return (
    <div className="backdrop" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="panel panel-rim w-full"
        style={{ maxWidth: 560, maxHeight: "90vh", overflowY: "auto", padding: "1.8rem" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="display" style={{ fontSize: "2rem" }}>Yeni otel</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Kapat"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <div>
            <label className="field-label" htmlFor="h-name"><Building2 size={14} className="text-[var(--violet-300)]" /> Otel adı *</label>
            <input
              id="h-name"
              className="field"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value;
                setForm((f) => ({ ...f, name, slug: f.slugTouched ? f.slug : slugify(name) }));
              }}
              required
            />
          </div>
          <div>
            <label className="field-label" htmlFor="h-slug"><LinkIcon size={14} className="text-[var(--violet-300)]" /> Otel kimliği (URL) *</label>
            <input
              id="h-slug"
              className="field mono"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: slugify(e.target.value), slugTouched: true })}
              required
            />
            <p className="field-hint">
              Misafir bağlantısı: <span className="mono text-[var(--violet-300)]">/{form.slug || "otel"}/oda-no</span>
            </p>
          </div>
          <div>
            <label className="field-label" htmlFor="h-pw"><KeyRound size={14} className="text-[var(--violet-300)]" /> Otel yönetici şifresi * (en az 8 karakter)</label>
            <input id="h-pw" type="password" className="field" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} autoComplete="new-password" required minLength={8} />
          </div>
          {fields.map((f) => (
            <div key={f.k}>
              <label className="field-label" htmlFor={`h-${f.k}`}><f.icon size={14} className="text-[var(--violet-300)]" /> {f.label}</label>
              <input id={`h-${f.k}`} type={f.type} className="field" placeholder={f.ph} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
            </div>
          ))}
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">Vazgeç</button>
            <button type="submit" disabled={isLoading} className="btn btn-primary flex-1">{isLoading ? "Oluşturuluyor…" : "Oteli oluştur"}</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ---------- Console ----------
export const MasterAdmin = () => {
  useSeo({ title: "RateGuard · Yönetim", noindex: true });
  const [token, setToken] = useState(() => storage.get("masterToken"));
  const [tab, setTab] = useState("hotels");
  const [hotels, setHotels] = useState([]);
  const [demos, setDemos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmSlug, setConfirmSlug] = useState(null);

  useEffect(() => applyDocLang("tr"), []);

  const headers = useMemo(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const handleLogout = useCallback(() => {
    storage.del("masterToken");
    setToken("");
    setHotels([]);
    setDemos([]);
  }, []);

  const isAuthError = (error) => [401, 403].includes(error?.response?.status);
  const sessionExpired = () => {
    toast.error("Oturum süresi doldu. Lütfen tekrar giriş yapın.");
    handleLogout();
  };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [h, d] = await Promise.all([
        axios.get(`${API}/master/hotels`, headers),
        axios.get(`${API}/master/demo-requests`, headers),
      ]);
      setHotels(h.data);
      setDemos(d.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) handleLogout();
    } finally {
      setIsLoading(false);
    }
  }, [token, headers, handleLogout]);

  useEffect(() => {
    if (token) fetchAll();
  }, [token, fetchAll]);

  const handleAddHotel = async (data) => {
    const { slugTouched, ...payload } = data;
    try {
      await axios.post(`${API}/master/hotels`, payload, headers);
      toast.success("Otel oluşturuldu");
      fetchAll();
    } catch (error) {
      if (isAuthError(error)) sessionExpired();
      else toast.error(error.response?.data?.detail || "Otel oluşturulamadı");
      throw error;
    }
  };

  const handleDeleteHotel = async (slug) => {
    setConfirmSlug(null);
    try {
      await axios.delete(`${API}/master/hotels/${slug}`, headers);
      toast.success("Otel silindi");
      setSelected(null);
      fetchAll();
    } catch (error) {
      if (isAuthError(error)) sessionExpired();
      else if (error.response?.status === 404) {
        toast.success("Otel zaten silinmiş");
        fetchAll();
      } else toast.error("Silinemedi. Lütfen tekrar deneyin.");
    }
  };

  const openHotel = async (slug) => {
    try {
      const res = await axios.get(`${API}/master/hotels/${slug}/stats`, headers);
      setSelected(res.data);
    } catch (error) {
      if (isAuthError(error)) sessionExpired();
      else toast.error("İstatistikler yüklenemedi");
    }
  };

  const setDemoStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/master/demo-requests/${id}?status=${status}`, {}, headers);
      setDemos((ds) => ds.map((d) => (d.id === id ? { ...d, status } : d)));
    } catch (error) {
      if (isAuthError(error)) sessionExpired();
      else toast.error("Güncellenemedi");
    }
  };

  const fmtDate = (s) => new Date(s).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });

  if (!token) {
    return (
      <MasterLogin
        onLogin={(tk) => {
          setIsLoading(true);
          setToken(tk);
        }}
      />
    );
  }
  if (isLoading) return <LoadingScreen />;

  const newDemos = demos.filter((d) => d.status === "new").length;

  return (
    <div className="scene" style={{ minHeight: "100dvh" }}>
      <header
        className="sticky z-30"
        style={{ top: "env(safe-area-inset-top, 0px)", background: "rgba(10,8,18,.75)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderBottom: "1px solid var(--line)" }}
      >
        <div className="wrap flex items-center justify-between gap-3" style={{ height: 64 }}>
          <div className="flex items-center gap-3">
            <Brand to="/master-admin" size={30} />
            <span className="chip chip-violet hidden sm:inline-flex">Konsol</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={fetchAll} className="icon-btn" title="Yenile" aria-label="Yenile"><RefreshCw size={18} /></button>
            <button onClick={handleLogout} className="icon-btn is-danger" title="Çıkış" aria-label="Çıkış"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="wrap" style={{ paddingBlock: "2rem 4rem" }}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
          <div>
            <div className="eyebrow">Platform</div>
            <h1 className="display" style={{ fontSize: "clamp(2.2rem, 5vw, 3rem)" }}>
              {hotels.length} otel · {demos.length} demo talebi
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="panel-flat inline-flex p-1 gap-1" style={{ borderRadius: 14 }} role="tablist">
              {[
                ["hotels", "Oteller", Building2],
                ["demos", `Demo talepleri${newDemos ? ` (${newDemos})` : ""}`, Inbox],
              ].map(([k, label, Icon]) => (
                <button
                  key={k}
                  role="tab"
                  aria-selected={tab === k}
                  onClick={() => setTab(k)}
                  className={tab === k ? "btn btn-primary btn-sm" : "btn btn-ghost"}
                  style={{ minHeight: 38 }}
                >
                  <Icon size={16} /> {label}
                </button>
              ))}
            </div>
            {tab === "hotels" && (
              <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-sm" data-testid="add-hotel-button">
                <Plus size={16} /> Otel ekle
              </button>
            )}
          </div>
        </div>

        {tab === "hotels" ? (
          <div className="grid lg:grid-cols-3 gap-6">
            <section className="lg:col-span-2 panel overflow-hidden">
              {hotels.length === 0 ? (
                <div className="text-center" style={{ padding: "3.5rem 1.5rem" }}>
                  <Building2 size={40} className="mx-auto mb-4 text-[var(--text-3)]" />
                  <p className="muted">Henüz otel yok</p>
                  <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-sm mt-5">İlk oteli ekle</button>
                </div>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {hotels.map((h) => (
                    <li
                      key={h.id}
                      className="flex items-center justify-between gap-3"
                      style={{
                        padding: "0.9rem 1.2rem",
                        borderBottom: "1px solid rgba(196,181,253,.06)",
                        background: selected?.hotel?.slug === h.slug ? "rgba(139,92,246,.10)" : "transparent",
                      }}
                    >
                      <button
                        onClick={() => openHotel(h.slug)}
                        className="flex items-center gap-3 min-w-0 text-left"
                        style={{ background: "none", border: 0, color: "inherit", cursor: "pointer", flex: 1, padding: 0 }}
                      >
                        <div className="step-token shrink-0" style={{ width: 42, height: 42, fontSize: "1.3rem", borderRadius: 12 }}>
                          {h.name.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold truncate">{h.name}</div>
                          <div className="mono faint text-sm truncate">/{h.slug}</div>
                        </div>
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="faint text-xs hidden sm:inline">{fmtDate(h.created_at)}</span>
                        {confirmSlug === h.slug ? (
                          <>
                            <button className="btn btn-sm" style={{ minHeight: 32, background: "var(--danger)", color: "#2a0610" }} onClick={() => handleDeleteHotel(h.slug)}>
                              Tüm veriyle sil
                            </button>
                            <button className="icon-btn" onClick={() => setConfirmSlug(null)} aria-label="Vazgeç"><X size={16} /></button>
                          </>
                        ) : (
                          <button onClick={() => setConfirmSlug(h.slug)} className="icon-btn is-danger" title="Sil" aria-label="Sil">
                            <Trash2 size={17} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <aside className="panel" style={{ padding: "1.6rem" }}>
              {selected ? (
                <>
                  <h3 className="display" style={{ fontSize: "1.8rem" }}>{selected.hotel.name}</h3>
                  <div className="grid gap-4 mt-5">
                    <div>
                      <div className="eyebrow faint mb-1">Misafir bağlantısı</div>
                      <a className="mono text-sm break-all text-[var(--violet-300)]" href={`/${selected.hotel.slug}/101`} target="_blank" rel="noopener noreferrer">
                        /{selected.hotel.slug}/101
                      </a>
                    </div>
                    <hr className="divider" />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="stat-value" style={{ fontSize: "2.2rem" }}>{selected.stats.total_feedback}</div>
                        <div className="stat-label">Geri bildirim</div>
                      </div>
                      <div>
                        <div className="stat-value" style={{ fontSize: "2.2rem", color: "var(--star)" }}>{selected.stats.average_rating.toFixed(1)}</div>
                        <div className="stat-label">Ortalama</div>
                      </div>
                    </div>
                    {selected.hotel.notification_email && (
                      <>
                        <hr className="divider" />
                        <div>
                          <div className="eyebrow faint mb-1">Bildirim e-postası</div>
                          <div className="text-sm">{selected.hotel.notification_email}</div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-10">
                  <Eye size={32} className="mx-auto mb-3 text-[var(--text-3)]" />
                  <p className="muted">Ayrıntılar için bir otel seçin</p>
                </div>
              )}
            </aside>
          </div>
        ) : (
          <section className="panel overflow-hidden">
            {demos.length === 0 ? (
              <div className="text-center" style={{ padding: "3.5rem 1.5rem" }}>
                <Inbox size={40} className="mx-auto mb-4 text-[var(--text-3)]" />
                <p className="muted">Henüz demo talebi yok. Ana sayfadaki form doldurulduğunda burada görünecek.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Otel</th>
                      <th>Kişi</th>
                      <th>İletişim</th>
                      <th>Not</th>
                      <th>Tarih</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demos.map((d) => (
                      <tr key={d.id}>
                        <td>
                          <div className="font-bold">{d.hotel_name}</div>
                          <div className="faint text-sm flex items-center gap-1">
                            {d.city && (<><MapPin size={12} /> {d.city}</>)}
                            {d.rooms && <span className="mono"> · {d.rooms} oda</span>}
                          </div>
                        </td>
                        <td>{d.name}</td>
                        <td className="text-sm">
                          {d.phone && <div className="flex items-center gap-1"><Phone size={12} className="text-[var(--violet-300)]" /> <span className="mono">{d.phone}</span></div>}
                          {d.email && <div className="flex items-center gap-1"><Mail size={12} className="text-[var(--violet-300)]" /> {d.email}</div>}
                        </td>
                        <td className="muted text-sm" style={{ maxWidth: 280 }}>{d.message || <span className="faint">—</span>}</td>
                        <td className="muted text-sm whitespace-nowrap">{fmtDate(d.created_at)}</td>
                        <td>
                          <select
                            value={d.status}
                            onChange={(e) => setDemoStatus(d.id, e.target.value)}
                            className="field"
                            style={{ minHeight: 36, padding: "0 0.6rem", fontSize: 13, width: "auto" }}
                            aria-label="Durum"
                          >
                            {DEMO_STATUSES.map((s) => (
                              <option key={s.v} value={s.v}>{s.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>

      {showAddModal && <AddHotelModal onClose={() => setShowAddModal(false)} onAdd={handleAddHotel} />}
    </div>
  );
};

export default MasterAdmin;
